import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, ChevronDown } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { categories } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const { cartCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isHome = location.pathname === '/';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-slate-900/95 backdrop-blur-xl shadow-xl' 
        : isHome ? 'bg-transparent' : 'bg-slate-900'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-vybe-500 to-vybe-700 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <span className="text-white font-black text-xl">K</span>
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-coral-500 rounded-full animate-pulse" />
            </div>
            <div>
              <span className="font-display text-2xl font-black text-white tracking-tight">
                KETO<span className="text-vybe-400">VYBE</span>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Home
            </Link>
            
            {/* Shop Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowCategories(!showCategories)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                  location.pathname.includes('/shop')
                    ? 'text-white bg-white/10'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                Shop
                <ChevronDown className={`w-4 h-4 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
              </button>
              
              {showCategories && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowCategories(false)} />
                  <div className="absolute top-full left-0 mt-2 w-56 bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 py-2 z-20 overflow-hidden">
                    <Link
                      to="/shop"
                      className="block px-5 py-3 text-white font-semibold hover:bg-vybe-600/20 border-b border-slate-700"
                    >
                      All Products
                    </Link>
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/shop?category=${cat.slug}`}
                        className="block px-5 py-2.5 text-slate-300 hover:text-white hover:bg-vybe-600/20 text-sm transition-colors"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/faq"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/faq'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              FAQ
            </Link>
            <Link
              to="/ingredients"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/ingredients'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Ingredients
            </Link>
            <Link
              to="/how-to-use"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/how-to-use'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              How to Use
            </Link>
            <Link
              to="/why-choose-us"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/why-choose-us'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Why Us
            </Link>
            <Link
              to="/guarantee"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/guarantee'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Guarantee
            </Link>
            <Link
              to="/contact"
              className={`px-4 py-2.5 text-sm font-semibold transition-all rounded-full ${
                location.pathname === '/contact'
                  ? 'text-white bg-white/10'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Auth */}
            <div className="hidden lg:flex items-center">
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-300">{user?.firstName}</span>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-slate-400 hover:text-white transition-colors"
                    title="Sign Out"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="font-medium">Sign In</span>
                </Link>
              )}
            </div>

            {/* Cart */}
            <button
              onClick={openCart}
              className="relative p-3 bg-gradient-to-r from-vybe-600 to-vybe-500 hover:from-vybe-500 hover:to-vybe-400 rounded-xl text-white transition-all shadow-button"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-coral-500 text-white text-xs font-bold flex items-center justify-center rounded-full shadow-lg">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-xl transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
        isMobileMenuOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="bg-slate-900 border-t border-slate-800 px-4 py-6 space-y-2">
          <Link
            to="/"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            Home
          </Link>
          <Link
            to="/shop"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/shop' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            Shop All
          </Link>
          
          <div className="pl-4 py-2 space-y-1">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/shop?category=${cat.slug}`}
                className="block px-3 py-2 text-sm text-slate-400 hover:text-vybe-400"
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <Link
            to="/faq"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/faq' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            FAQ
          </Link>
          <Link
            to="/ingredients"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/ingredients' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            Ingredients
          </Link>
          <Link
            to="/how-to-use"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/how-to-use' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            How to Use
          </Link>
          <Link
            to="/why-choose-us"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/why-choose-us' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            Why Choose Us
          </Link>
          <Link
            to="/guarantee"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/guarantee' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            Guarantee
          </Link>
          <Link
            to="/contact"
            className={`block px-4 py-3 rounded-xl font-semibold ${
              location.pathname === '/contact' ? 'bg-vybe-600/20 text-vybe-400' : 'text-white hover:bg-slate-800'
            }`}
          >
            Contact
          </Link>
          
          <div className="pt-4 border-t border-slate-800 mt-4">
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-3 text-white hover:bg-slate-800 rounded-xl"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-3 px-4 py-3 text-white hover:bg-slate-800 rounded-xl"
              >
                <User className="w-5 h-5" />
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
